import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type InterruptedToolAction,
  interruptedToolAction,
  resumeActionPrompt,
  skipActionPrompt,
} from '@/lib/conversations/message-part/interrupted-action';
import type { InterruptedActionValue } from '@/lib/conversations/message-part/interrupted-action-context';

/**
 * Wait after the session is ready before offering a manual resume, so a capable
 * agent that re-issues the permission itself on `session/load` wins the race and
 * the live prompt (not our card) handles it.
 */
const RESUME_GRACE_MS = 1200;

/**
 * Builds the resume affordance for a cut-off tool call at the tail of a reopened
 * conversation. The card is offered only once the connection is ready, no live
 * tool approval is pending (the agent did not re-ask), the conversation is idle,
 * and the grace window has elapsed. Answering sends a deterministic continuation
 * to the agent rather than resurrecting the original (now-dead) live request.
 */
export function useInterruptedActionResume(options: {
  messages: readonly CrowdexUIMessage[];
  ready: boolean;
  hasPendingToolApproval: boolean;
  busy: boolean;
  /** `answers`: the question tool call the message answers (sent through its claim). */
  sendMessage: (message: { text: string; answers?: string }) => void;
  recordQuestionAnswer: (toolCallId: string, answer: string) => void;
}): InterruptedActionValue {
  const { messages, ready, hasPendingToolApproval, busy, sendMessage, recordQuestionAnswer } =
    options;
  const action = useMemo(() => interruptedToolAction(messages), [messages]);
  const [resolvedIds, setResolvedIds] = useState<ReadonlySet<string>>(new Set());
  const [graceElapsed, setGraceElapsed] = useState(false);

  const candidateId = action !== null && ready && !busy ? action.toolCallId : null;
  useEffect(() => {
    if (candidateId === null) {
      setGraceElapsed(false);
      return;
    }
    setGraceElapsed(false);
    const timer = window.setTimeout(() => setGraceElapsed(true), RESUME_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [candidateId]);

  const onResume = useCallback(
    (resumed: InterruptedToolAction) => {
      setResolvedIds((current) => new Set(current).add(resumed.toolCallId));
      sendMessage({ text: resumeActionPrompt(resumed) });
    },
    [sendMessage],
  );
  const onSkip = useCallback(
    (skipped: InterruptedToolAction) => {
      setResolvedIds((current) => new Set(current).add(skipped.toolCallId));
      sendMessage({ text: skipActionPrompt(skipped) });
    },
    [sendMessage],
  );
  const onAnswer = useCallback(
    (toolCallId: string, answer: string) => {
      const text = answer.trim();
      if (text.length === 0) return;
      setResolvedIds((current) => new Set(current).add(toolCallId));
      recordQuestionAnswer(toolCallId, text);
      sendMessage({ text });
    },
    [recordQuestionAnswer, sendMessage],
  );
  const onAnswerQuestion = useCallback(
    (toolCallId: string, answer: string) => {
      const text = answer.trim();
      if (text.length === 0) return;
      recordQuestionAnswer(toolCallId, text);
      sendMessage({ text, answers: toolCallId });
    },
    [recordQuestionAnswer, sendMessage],
  );

  const activeToolCallId =
    action !== null &&
    ready &&
    !busy &&
    !hasPendingToolApproval &&
    graceElapsed &&
    !resolvedIds.has(action.toolCallId)
      ? action.toolCallId
      : null;

  return useMemo(
    () => ({ activeToolCallId, onResume, onSkip, onAnswer, onAnswerQuestion }),
    [activeToolCallId, onAnswer, onAnswerQuestion, onResume, onSkip],
  );
}
